// Assessment Week Workout Plans
// Week 1 is focused on gathering baseline data for AI personalization
// Different assessment protocols based on user's primary goal

export const assessmentWeeks = {
  // For users with primary goal: muscle building / hypertrophy
  muscle: {
    programName: "Week 1: Muscle Building Assessment",
    description: "This week establishes your baseline strength, volume capacity, and recovery ability to design your muscle building program.",
    assessmentFocus: [
      "Maximum reps at bodyweight exercises",
      "Strength baseline with moderate weights (8-12 rep range)",
      "Volume tolerance and recovery capacity",
      "Mind-muscle connection and form quality"
    ],

    monday: {
      name: "Upper Body Push Assessment",
      time: "Morning",
      duration: "45-55 min",
      location: "Gym or Home",
      focus: "Chest, Shoulders, Triceps - Baseline Testing",
      calorieGoal: 2400,
      proteinGoal: 170,
      nutritionNotes: "Training day - eat normally, focus on protein",
      exercises: [
        {
          id: 1,
          name: "Push-ups - Max Rep Test",
          sets: 1,
          reps: "MAX (record number)",
          rest: "3 min after",
          notes: "Go to complete failure with perfect form. This establishes your baseline.",
          howTo: "Standard push-ups, chest to ground, full lockout. Stop when form breaks down.",
          assessmentMetric: "max_reps",
          progressionTips: "Record this number - goal is +5 reps by Week 4"
        },
        {
          id: 2,
          name: "Push-ups - Volume Test",
          sets: 3,
          reps: "50% of max",
          rest: "90 sec",
          notes: "If you did 20 max, do 3 sets of 10. Tests volume capacity.",
          howTo: "Same form as max test, but stop at 50% of your max reps",
          assessmentMetric: "volume_capacity"
        },
        {
          id: 3,
          name: "Dumbbell Bench Press or Floor Press (Home)",
          sets: 3,
          reps: "Find your 10-12 rep max",
          rest: "2 min",
          notes: "Start light, add weight each set until you find a weight where 10-12 reps is challenging but doable with good form",
          howTo: "Lie on bench or floor, press dumbbells up. Lower with control. Find weight that challenges you at 10-12 reps.",
          assessmentMetric: "strength_baseline",
          progressionTips: "Record the weight - this is your starting point"
        },
        {
          id: 4,
          name: "Overhead Press (Dumbbells or Machine)",
          sets: 3,
          reps: "Find your 8-10 rep max",
          rest: "2 min",
          notes: "Test shoulder pressing strength",
          howTo: "Press dumbbells or machine overhead. Find challenging weight for 8-10 reps.",
          assessmentMetric: "strength_baseline"
        },
        {
          id: 5,
          name: "Lateral Raises",
          sets: 3,
          reps: "12-15",
          rest: "60 sec",
          notes: "Light weight, focus on form and mind-muscle connection",
          howTo: "Raise dumbbells to shoulder height, slight bend in elbows, control the descent"
        },
        {
          id: 6,
          name: "Tricep Dips or Pushdowns",
          sets: 3,
          reps: "10-15",
          rest: "60 sec",
          notes: "Test tricep endurance",
          howTo: "Dips: Lower until upper arms parallel to ground. Pushdowns: Full extension, squeeze at bottom."
        }
      ]
    },

    tuesday: {
      name: "Lower Body Assessment",
      time: "Morning",
      duration: "50-60 min",
      location: "Gym or Home",
      focus: "Legs, Glutes - Baseline Testing",
      calorieGoal: 2400,
      proteinGoal: 170,
      nutritionNotes: "Training day - carbs for leg day energy",
      exercises: [
        {
          id: 1,
          name: "Bodyweight Squats - Max Rep Test",
          sets: 1,
          reps: "MAX (record number)",
          rest: "3 min",
          notes: "Deep squats to failure. Establishes leg endurance baseline.",
          howTo: "Squat until thighs parallel or below, stand fully. Count total reps until form breaks.",
          assessmentMetric: "max_reps"
        },
        {
          id: 2,
          name: "Goblet Squat or Leg Press",
          sets: 3,
          reps: "Find your 12-15 rep max",
          rest: "2 min",
          notes: "Find a challenging weight for higher reps - tests muscle endurance",
          howTo: "Goblet: Hold dumbbell at chest, squat deep. Leg Press: Feet shoulder-width, push through heels.",
          assessmentMetric: "strength_baseline"
        },
        {
          id: 3,
          name: "Romanian Deadlift (Dumbbells or Barbell)",
          sets: 3,
          reps: "Find your 10-12 rep max",
          rest: "2 min",
          notes: "Critical for glute/hamstring development - find your baseline",
          howTo: "Hip hinge, slight knee bend, lower weight along shins, feel hamstring stretch. Return to standing.",
          assessmentMetric: "strength_baseline",
          progressionTips: "This exercise is KEY for glute growth - record your starting weight"
        },
        {
          id: 4,
          name: "Bulgarian Split Squats",
          sets: 3,
          reps: "10-12 per leg (bodyweight or light weight)",
          rest: "90 sec",
          notes: "Tests single-leg strength and balance",
          howTo: "Back foot elevated on bench, lunge down on front leg. Keep torso upright."
        },
        {
          id: 5,
          name: "Leg Curl (Machine or Nordic if advanced)",
          sets: 3,
          reps: "12-15",
          rest: "60 sec",
          notes: "Hamstring isolation baseline",
          howTo: "Lie face down, curl heels to glutes, control the negative"
        },
        {
          id: 6,
          name: "Calf Raises",
          sets: 3,
          reps: "15-20",
          rest: "45 sec",
          notes: "Test calf endurance",
          howTo: "Rise onto toes, full contraction at top, stretch at bottom"
        }
      ]
    },

    wednesday: {
      name: "Rest Day",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Recovery & Assessment",
      calorieGoal: 2100,
      proteinGoal: 170,
      nutritionNotes: "Rest day - maintain protein, reduce carbs slightly",
      exercises: [],
      recoveryNotes: "How did you recover from Monday and Tuesday? Note soreness levels, energy, sleep quality. This helps assess your recovery capacity."
    },

    thursday: {
      name: "Upper Body Pull Assessment",
      time: "Morning",
      duration: "45-55 min",
      location: "Gym or Home",
      focus: "Back, Biceps - Baseline Testing",
      calorieGoal: 2400,
      proteinGoal: 170,
      exercises: [
        {
          id: 1,
          name: "Pull-ups or Chin-ups - Max Rep Test",
          sets: 1,
          reps: "MAX (record number, use assistance if needed)",
          rest: "3 min",
          notes: "Critical assessment. Even if you can only do 1-2, or need assistance, record it.",
          howTo: "Dead hang, pull until chin over bar, lower with control. Use resistance band for assistance if needed.",
          assessmentMetric: "max_reps",
          progressionTips: "This is your primary back strength marker"
        },
        {
          id: 2,
          name: "Pull-ups - Volume Test (or Negative Pull-ups)",
          sets: 3,
          reps: "50% of max OR 3-5 negatives",
          rest: "2 min",
          notes: "If you did 6 pull-ups, do 3 sets of 3. If you did 0-2, do slow negatives.",
          howTo: "Negatives: Jump to top position, lower yourself as slowly as possible (5-10 seconds)",
          assessmentMetric: "volume_capacity"
        },
        {
          id: 3,
          name: "Barbell or Dumbbell Row",
          sets: 3,
          reps: "Find your 8-10 rep max",
          rest: "2 min",
          notes: "Horizontal pulling strength baseline",
          howTo: "Bent over row: Pull weight to lower chest, squeeze shoulder blades. Keep back flat.",
          assessmentMetric: "strength_baseline"
        },
        {
          id: 4,
          name: "Seated Cable Row or Inverted Rows",
          sets: 3,
          reps: "12-15",
          rest: "90 sec",
          notes: "Higher rep back work",
          howTo: "Cable row: Pull to torso, elbows back. Inverted row: Set bar at waist height, pull chest to bar."
        },
        {
          id: 5,
          name: "Face Pulls",
          sets: 3,
          reps: "15-20",
          rest: "60 sec",
          notes: "Shoulder health and rear delt development",
          howTo: "Pull rope to face, elbows high, rotate externally at end"
        },
        {
          id: 6,
          name: "Bicep Curls (Barbell or Dumbbell)",
          sets: 3,
          reps: "10-12",
          rest: "60 sec",
          notes: "Find a weight that challenges you in this range",
          howTo: "Curl weight without swinging, full contraction at top, control descent"
        }
      ]
    },

    friday: {
      name: "Full Body Circuit - Work Capacity Test",
      time: "Morning",
      duration: "40-50 min",
      location: "Gym or Home",
      focus: "Overall Work Capacity & Recovery Assessment",
      calorieGoal: 2400,
      proteinGoal: 170,
      nutritionNotes: "Last training day - good meal before and after",
      exercises: [
        {
          id: 1,
          name: "Circuit Round 1: Push-ups",
          sets: 3,
          reps: "60-70% of max",
          rest: "Minimal (move to next exercise)",
          notes: "Perform as a circuit - all 6 exercises back-to-back, then rest 2-3 min",
          howTo: "Standard push-ups, maintain form"
        },
        {
          id: 2,
          name: "Circuit Round 1: Goblet Squats",
          sets: 3,
          reps: "12-15",
          rest: "Minimal",
          notes: "Moderate weight",
          howTo: "Squat deep with dumbbell at chest"
        },
        {
          id: 3,
          name: "Circuit Round 1: Dumbbell Rows",
          sets: 3,
          reps: "10-12 per arm",
          rest: "Minimal",
          notes: "Moderate weight",
          howTo: "One arm at a time, pull to hip"
        },
        {
          id: 4,
          name: "Circuit Round 1: Lunges",
          sets: 3,
          reps: "10 per leg",
          rest: "Minimal",
          notes: "Bodyweight or light dumbbells",
          howTo: "Alternating forward lunges"
        },
        {
          id: 5,
          name: "Circuit Round 1: Plank Hold",
          sets: 3,
          reps: "45-60 sec",
          rest: "Minimal",
          notes: "Core stability test",
          howTo: "Forearms and toes, body straight"
        },
        {
          id: 6,
          name: "Circuit Round 1: Mountain Climbers",
          sets: 3,
          reps: "30 sec fast",
          rest: "2-3 min after complete round",
          notes: "Complete all 6 exercises, THEN rest 2-3 min. Repeat for 3 total rounds.",
          howTo: "Plank position, drive knees to chest alternating",
          assessmentMetric: "work_capacity",
          progressionTips: "How quickly did you complete? How hard was it? This tests your conditioning."
        }
      ]
    },

    saturday: {
      name: "Active Recovery",
      time: "Flexible",
      duration: "20-30 min",
      location: "Home or Outside",
      focus: "Light Movement & Reflection",
      calorieGoal: 2100,
      proteinGoal: 170,
      exercises: [
        {
          id: 1,
          name: "Light Walk or Bike",
          sets: 1,
          reps: "20-30 min",
          rest: "N/A",
          notes: "Easy pace, promote recovery",
          howTo: "Conversational pace, enjoy the movement"
        },
        {
          id: 2,
          name: "Stretching Session",
          sets: 1,
          reps: "15-20 min",
          rest: "N/A",
          notes: "Full body stretching, note tight areas",
          howTo: "Hold each stretch 30-60 seconds, breathe deeply"
        }
      ]
    },

    sunday: {
      name: "Rest & Weekly Review",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Complete Rest & Data Review",
      calorieGoal: 2100,
      proteinGoal: 170,
      exercises: [],
      recoveryNotes: "Review your week: How was your energy? Recovery? Which exercises felt strong vs weak? This data will be used to design your personalized program."
    }
  },

  // For users with primary goal: fat loss / weight loss / toning
  fatLoss: {
    programName: "Week 1: Fat Loss Assessment",
    description: "This week establishes your cardiovascular baseline, movement capacity, and calorie burn potential.",
    assessmentFocus: [
      "Cardiovascular endurance baseline",
      "Work capacity and circuit performance",
      "Movement quality and injury assessment",
      "Calorie burn and recovery capacity"
    ],

    monday: {
      name: "Cardio Endurance Assessment",
      time: "Morning",
      duration: "35-45 min",
      location: "Gym or Outside",
      focus: "Aerobic Capacity Baseline",
      calorieGoal: 1900,
      proteinGoal: 150,
      nutritionNotes: "Moderate deficit - focus on protein and vegetables",
      exercises: [
        {
          id: 1,
          name: "12-Minute Run/Walk Test",
          sets: 1,
          reps: "12 minutes continuous",
          rest: "5 min after",
          notes: "Go as far as you can in 12 minutes. Walk when needed, but keep moving. Record distance.",
          howTo: "Track/treadmill: Maintain steady pace for 12 min. Record total distance covered.",
          assessmentMetric: "cardio_baseline",
          progressionTips: "This distance will increase dramatically as you lose weight and improve fitness"
        },
        {
          id: 2,
          name: "Bodyweight Squats - Endurance Test",
          sets: 1,
          reps: "MAX in 2 minutes",
          rest: "3 min",
          notes: "How many squats can you do in 2 minutes? Tests muscular endurance.",
          howTo: "Continuous squats for 2 min, rest as needed but keep time running",
          assessmentMetric: "muscular_endurance"
        },
        {
          id: 3,
          name: "Push-ups - Max Rep Test",
          sets: 1,
          reps: "MAX (knee push-ups OK)",
          rest: "2 min",
          notes: "Upper body endurance baseline",
          howTo: "Standard or knee push-ups, go to failure with good form",
          assessmentMetric: "upper_body_endurance"
        },
        {
          id: 4,
          name: "Plank Hold - Max Time",
          sets: 1,
          reps: "MAX time",
          rest: "2 min",
          notes: "Core endurance - hold as long as possible with good form",
          howTo: "Forearms and toes, straight body, stop when hips sag",
          assessmentMetric: "core_endurance"
        },
        {
          id: 5,
          name: "Burpees - 1 Minute Test",
          sets: 1,
          reps: "MAX in 60 seconds",
          rest: "Complete",
          notes: "Full body conditioning marker - count total reps",
          howTo: "Squat, hands down, jump feet back, push-up (optional), jump feet in, jump up",
          assessmentMetric: "conditioning_baseline",
          progressionTips: "Track this number - great overall fitness indicator"
        }
      ]
    },

    tuesday: {
      name: "Metabolic Circuit Assessment",
      time: "Morning",
      duration: "40-50 min",
      location: "Gym or Home",
      focus: "Work Capacity & Calorie Burn",
      calorieGoal: 1900,
      proteinGoal: 150,
      exercises: [
        {
          id: 1,
          name: "Full Body Circuit - Timed",
          sets: 4,
          reps: "40 sec work / 20 sec rest per exercise",
          rest: "2 min between complete circuits",
          notes: "Complete all 8 exercises back-to-back, then rest. Repeat 4 rounds total. Track how many total reps you complete.",
          howTo: "Set timer: 40 sec work, 20 sec transition. Move through all exercises.",
          assessmentMetric: "work_capacity"
        },
        {
          id: 2,
          name: "Jump Rope or High Knees",
          sets: 4,
          reps: "40 sec",
          rest: "20 sec",
          notes: "Part of circuit - cardio burst",
          howTo: "Jump rope if available, otherwise high knees running in place"
        },
        {
          id: 3,
          name: "Bodyweight Squats",
          sets: 4,
          reps: "40 sec",
          rest: "20 sec",
          notes: "As many reps as possible in 40 seconds",
          howTo: "Continuous squats, full depth"
        },
        {
          id: 4,
          name: "Mountain Climbers",
          sets: 4,
          reps: "40 sec",
          rest: "20 sec",
          notes: "Fast pace",
          howTo: "Plank position, drive knees to chest rapidly"
        },
        {
          id: 5,
          name: "Push-ups (any variation)",
          sets: 4,
          reps: "40 sec",
          rest: "20 sec",
          notes: "Knee push-ups are fine",
          howTo: "As many push-ups as possible in 40 seconds"
        },
        {
          id: 6,
          name: "Jumping Jacks",
          sets: 4,
          reps: "40 sec",
          rest: "20 sec",
          notes: "Keep moving",
          howTo: "Standard jumping jacks, maintain pace"
        },
        {
          id: 7,
          name: "Reverse Lunges",
          sets: 4,
          reps: "40 sec (alternating legs)",
          rest: "20 sec",
          notes: "Bodyweight, alternating legs",
          howTo: "Step back into lunge, alternate legs continuously"
        },
        {
          id: 8,
          name: "Bicycle Crunches",
          sets: 4,
          reps: "40 sec",
          rest: "20 sec",
          notes: "Core finisher",
          howTo: "Opposite elbow to knee, continuous motion"
        },
        {
          id: 9,
          name: "Burpees",
          sets: 4,
          reps: "40 sec",
          rest: "2 min after complete round",
          notes: "Final exercise in circuit - push hard then rest 2 min before next round",
          howTo: "Full burpee with jump, or step-back version if needed"
        }
      ]
    },

    wednesday: {
      name: "Rest Day",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Recovery Assessment",
      calorieGoal: 1700,
      proteinGoal: 150,
      nutritionNotes: "Rest day - lower carbs, maintain protein for recovery",
      exercises: [],
      recoveryNotes: "How sore are you? Energy levels? Sleep quality? This helps us understand your recovery needs for fat loss programming."
    },

    thursday: {
      name: "Strength Endurance Assessment",
      time: "Morning",
      duration: "45-50 min",
      location: "Gym or Home",
      focus: "Muscle Preservation During Fat Loss",
      calorieGoal: 1900,
      proteinGoal: 150,
      exercises: [
        {
          id: 1,
          name: "Dumbbell or Machine Chest Press",
          sets: 3,
          reps: "15-20 (find challenging weight)",
          rest: "75 sec",
          notes: "Higher reps for fat loss, but still challenging",
          howTo: "Find a weight where 15-20 reps is difficult but achievable",
          assessmentMetric: "strength_endurance"
        },
        {
          id: 2,
          name: "Goblet Squats",
          sets: 3,
          reps: "20-25",
          rest: "75 sec",
          notes: "High rep leg work - great for fat loss",
          howTo: "Hold dumbbell at chest, squat deep, higher reps",
          assessmentMetric: "leg_endurance"
        },
        {
          id: 3,
          name: "Dumbbell Rows",
          sets: 3,
          reps: "15-20 per arm",
          rest: "75 sec",
          notes: "Back strength preservation",
          howTo: "One arm at a time, pull to hip, control the weight"
        },
        {
          id: 4,
          name: "Walking Lunges",
          sets: 3,
          reps: "20-30 total steps",
          rest: "75 sec",
          notes: "Bodyweight or light dumbbells",
          howTo: "Long strides, continuous walking lunges"
        },
        {
          id: 5,
          name: "Shoulder Press",
          sets: 3,
          reps: "12-15",
          rest: "60 sec",
          notes: "Moderate weight, higher reps",
          howTo: "Dumbbells or machine, press overhead, control descent"
        },
        {
          id: 6,
          name: "Plank to Downdog Flow",
          sets: 3,
          reps: "10-15 reps",
          rest: "60 sec",
          notes: "Core and shoulder mobility",
          howTo: "Plank position, push hips up into downward dog, return to plank"
        }
      ]
    },

    friday: {
      name: "Interval Training Assessment",
      time: "Morning",
      duration: "30-40 min",
      location: "Gym, Outside, or Home",
      focus: "HIIT Capacity & Fat Burning",
      calorieGoal: 1900,
      proteinGoal: 150,
      exercises: [
        {
          id: 1,
          name: "HIIT Intervals - Sprint/Walk",
          sets: 8,
          reps: "30 sec sprint / 90 sec walk",
          rest: "See notes",
          notes: "8 total rounds. Sprint = 80-90% effort, Walk = full recovery. Track how you feel in later rounds.",
          howTo: "Treadmill, bike, or outdoor running. 30 sec hard effort, 90 sec easy recovery.",
          assessmentMetric: "hiit_capacity",
          progressionTips: "Can you maintain intensity in rounds 6-8? This shows conditioning level."
        },
        {
          id: 2,
          name: "Bodyweight Tabata Circuit",
          sets: 4,
          reps: "20 sec work / 10 sec rest (4 exercises)",
          rest: "1 min between circuits",
          notes: "4 exercises × 2 rounds each = 4 minutes per circuit. Do 4 total circuits.",
          howTo: "Set Tabata timer: 20 sec max effort, 10 sec rest. Cycle through 4 exercises twice.",
          assessmentMetric: "tabata_performance"
        },
        {
          id: 3,
          name: "Tabata: Jump Squats or Squats",
          sets: 8,
          reps: "20 sec MAX",
          rest: "10 sec",
          notes: "Max reps in 20 seconds",
          howTo: "Explosive jump squats or fast bodyweight squats"
        },
        {
          id: 4,
          name: "Tabata: Push-ups",
          sets: 8,
          reps: "20 sec MAX",
          rest: "10 sec",
          notes: "Max reps in 20 seconds",
          howTo: "Any push-up variation you can maintain"
        },
        {
          id: 5,
          name: "Tabata: Mountain Climbers",
          sets: 8,
          reps: "20 sec MAX",
          rest: "10 sec",
          notes: "Fast pace",
          howTo: "Rapid mountain climbers"
        },
        {
          id: 6,
          name: "Tabata: Burpees",
          sets: 8,
          reps: "20 sec MAX",
          rest: "1 min after complete circuit",
          notes: "Hardest exercise last",
          howTo: "Max burpees in 20 seconds, full effort"
        }
      ]
    },

    saturday: {
      name: "Long Slow Cardio Test",
      time: "Morning",
      duration: "45-60 min",
      location: "Outside or Gym",
      focus: "Aerobic Base & Fat Burning Zone",
      calorieGoal: 1900,
      proteinGoal: 150,
      exercises: [
        {
          id: 1,
          name: "Steady State Cardio - Time Trial",
          sets: 1,
          reps: "45-60 min at conversational pace",
          rest: "N/A",
          notes: "Can you maintain movement for 45+ minutes? This is your aerobic base. Track distance or time.",
          howTo: "Walk, jog, bike, or elliptical. Pace where you can talk but are working. Track total distance/time.",
          assessmentMetric: "aerobic_endurance",
          progressionTips: "This baseline shows your current aerobic capacity"
        },
        {
          id: 2,
          name: "Mobility & Stretching",
          sets: 1,
          reps: "15 min",
          rest: "N/A",
          notes: "Post-cardio stretching - note tight areas",
          howTo: "Full body stretching, hold each 30-60 sec"
        }
      ]
    },

    sunday: {
      name: "Rest & Weekly Review",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Complete Rest & Assessment Review",
      calorieGoal: 1700,
      proteinGoal: 150,
      exercises: [],
      recoveryNotes: "Review: How was your energy this week? Were you too hungry? Could you maintain the pace? This data designs your personalized fat loss program."
    }
  },

  // For users with primary goal: strength / powerlifting
  strength: {
    programName: "Week 1: Strength Assessment",
    description: "This week establishes your baseline strength across major lifts using conservative weight to assess 1RM estimates and movement quality.",
    assessmentFocus: [
      "1RM estimates for major compound lifts",
      "Movement quality and form assessment",
      "Strength imbalances between sides/movements",
      "Power output and explosiveness"
    ],

    monday: {
      name: "Lower Body Strength Assessment",
      time: "Morning",
      duration: "60-70 min",
      location: "Gym (requires barbell/weights)",
      focus: "Squat and Deadlift Baseline",
      calorieGoal: 2600,
      proteinGoal: 180,
      nutritionNotes: "Strength training day - eat for performance, carbs pre-workout",
      exercises: [
        {
          id: 1,
          name: "Barbell Back Squat - 1RM Estimate",
          sets: 5,
          reps: "Ramp to heavy 3 reps",
          rest: "3-4 min",
          notes: "Start light (just bar), add weight each set. Final set: heaviest weight you can do for 3 clean reps. Don't go to failure.",
          howTo: "Set 1: Bar x10 (warmup), Set 2: 135x5, Set 3: Add 20-40lbs x3, Set 4: Add 20-40lbs x3, Set 5: Heavy 3 reps (should be challenging but doable)",
          assessmentMetric: "squat_3rm",
          progressionTips: "Record your heavy 3-rep weight. Your estimated 1RM = (3RM weight × 1.08)"
        },
        {
          id: 2,
          name: "Pause Squats (lighter)",
          sets: 3,
          reps: "5 reps (3-second pause at bottom)",
          rest: "2-3 min",
          notes: "Use 60-70% of your heavy weight from previous. Tests control and stability.",
          howTo: "Squat down, pause for 3 seconds at bottom, explode up",
          assessmentMetric: "movement_quality"
        },
        {
          id: 3,
          name: "Barbell Romanian Deadlift - Working Weight",
          sets: 4,
          reps: "Ramp to heavy 5 reps",
          rest: "2-3 min",
          notes: "Build to heaviest 5 reps with perfect form. Tests posterior chain strength.",
          howTo: "Hip hinge, bar stays close to legs, feel hamstring stretch, don't round back",
          assessmentMetric: "rdl_5rm"
        },
        {
          id: 4,
          name: "Bulgarian Split Squats",
          sets: 3,
          reps: "8 per leg",
          rest: "90 sec",
          notes: "Moderate weight - test for leg strength imbalance between sides",
          howTo: "Back foot elevated, hold dumbbells, note if one leg is significantly weaker",
          assessmentMetric: "bilateral_balance"
        },
        {
          id: 5,
          name: "Leg Press - High Rep Test",
          sets: 2,
          reps: "20 reps",
          rest: "2 min",
          notes: "Finish with volume work - find weight for challenging 20 reps",
          howTo: "Full range of motion, controlled reps"
        },
        {
          id: 6,
          name: "Ab Wheel or Hanging Leg Raises",
          sets: 3,
          reps: "Max reps or 10-15",
          rest: "90 sec",
          notes: "Core strength for bracing during heavy lifts",
          howTo: "Ab wheel: Control the rollout and return. Leg raises: No swinging."
        }
      ]
    },

    tuesday: {
      name: "Upper Body Press Assessment",
      time: "Morning",
      duration: "60-70 min",
      location: "Gym",
      focus: "Bench Press and Overhead Press Baseline",
      calorieGoal: 2600,
      proteinGoal: 180,
      exercises: [
        {
          id: 1,
          name: "Barbell Bench Press - 1RM Estimate",
          sets: 5,
          reps: "Ramp to heavy 3 reps",
          rest: "3-4 min",
          notes: "Primary assessment lift. Build conservatively to heavy 3 reps.",
          howTo: "Set 1: Bar x15, Set 2: 95lbs x8, Set 3: 135lbs x5, Set 4: Add weight x3, Set 5: Heavy 3 reps",
          assessmentMetric: "bench_3rm",
          progressionTips: "Record weight. Est 1RM = weight × 1.08. This is your strength baseline."
        },
        {
          id: 2,
          name: "Bench Press - Volume Drop Set",
          sets: 3,
          reps: "8 reps",
          rest: "2-3 min",
          notes: "Drop to 70-75% of heavy weight, do 3x8. Tests volume capacity.",
          howTo: "Controlled descent, explosive press, full lockout",
          assessmentMetric: "volume_capacity"
        },
        {
          id: 3,
          name: "Overhead Press (Barbell or Dumbbell) - 5RM Test",
          sets: 4,
          reps: "Ramp to heavy 5 reps",
          rest: "3 min",
          notes: "Build to heaviest strict 5 reps. No leg drive.",
          howTo: "Bar starts at shoulders, press straight up, full lockout overhead",
          assessmentMetric: "ohp_5rm",
          progressionTips: "OHP is great indicator of overall pressing strength"
        },
        {
          id: 4,
          name: "Incline Dumbbell Press",
          sets: 3,
          reps: "10 reps",
          rest: "2 min",
          notes: "Find 10-rep max weight",
          howTo: "Bench at 30-45 degrees, press dumbbells up, control descent"
        },
        {
          id: 5,
          name: "Dips (weighted if possible)",
          sets: 3,
          reps: "Max reps or 8-12",
          rest: "2 min",
          notes: "Bodyweight or add weight if you can do 12+ bodyweight reps",
          howTo: "Lean slightly forward for chest emphasis, full range of motion",
          assessmentMetric: "dip_strength"
        },
        {
          id: 6,
          name: "Tricep Work (Close Grip Bench or Pushdowns)",
          sets: 3,
          reps: "12-15",
          rest: "90 sec",
          notes: "Tricep strength supports main pressing",
          howTo: "Choose one: close grip bench or cable pushdowns, control the negative"
        }
      ]
    },

    wednesday: {
      name: "Rest Day",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Recovery from Heavy Lifts",
      calorieGoal: 2400,
      proteinGoal: 180,
      nutritionNotes: "Rest day - maintain high protein for recovery from heavy loads",
      exercises: [],
      recoveryNotes: "How do you feel after heavy squats and bench? Adequate recovery is crucial for strength gains. Note soreness and energy levels."
    },

    thursday: {
      name: "Upper Body Pull Assessment",
      time: "Morning",
      duration: "55-65 min",
      location: "Gym",
      focus: "Deadlift and Row Strength",
      calorieGoal: 2600,
      proteinGoal: 180,
      exercises: [
        {
          id: 1,
          name: "Conventional Deadlift - 1RM Estimate",
          sets: 5,
          reps: "Ramp to heavy 3 reps",
          rest: "4-5 min",
          notes: "THE strength test. Build conservatively, prioritize form. Stop at heavy 3 reps.",
          howTo: "Set 1: 135x8, Set 2: 185x5, Set 3: 225x3, Set 4: Add 20-40lbs x3, Set 5: Heavy 3 reps",
          assessmentMetric: "deadlift_3rm",
          progressionTips: "King of strength exercises. Record this weight - major baseline marker."
        },
        {
          id: 2,
          name: "Deficit Deadlifts or Romanian Deadlifts",
          sets: 3,
          reps: "6 reps",
          rest: "3 min",
          notes: "Use 60-70% of heavy deadlift weight. Tests hamstring/low back strength.",
          howTo: "Stand on 1-2 inch platform OR do RDLs. Focus on form and stretch.",
          assessmentMetric: "posterior_strength"
        },
        {
          id: 3,
          name: "Barbell Row - Working Weight Test",
          sets: 4,
          reps: "Ramp to heavy 6 reps",
          rest: "2-3 min",
          notes: "Build to heaviest strict 6 reps. No body english.",
          howTo: "Bent over 45 degrees, pull bar to lower chest/upper abs, squeeze back",
          assessmentMetric: "row_6rm"
        },
        {
          id: 4,
          name: "Pull-ups or Weighted Pull-ups",
          sets: 4,
          reps: "Max reps (or 5-8 weighted)",
          rest: "2-3 min",
          notes: "If you can do 10+ bodyweight, add weight. Critical vertical pull strength.",
          howTo: "Dead hang, pull chin over bar, full lockout at bottom",
          assessmentMetric: "pullup_max",
          progressionTips: "Pull-up strength correlates with overall back development"
        },
        {
          id: 5,
          name: "Face Pulls",
          sets: 3,
          reps: "15-20",
          rest: "90 sec",
          notes: "Rear delt and rotator cuff health for heavy pressing",
          howTo: "Pull rope to face, elbows high, external rotation at end"
        },
        {
          id: 6,
          name: "Barbell or Dumbbell Curls",
          sets: 3,
          reps: "10-12",
          rest: "90 sec",
          notes: "Bicep strength supports pulling",
          howTo: "Strict form, no swinging, full contraction"
        }
      ]
    },

    friday: {
      name: "Power and Accessory Assessment",
      time: "Morning",
      duration: "50-60 min",
      location: "Gym",
      focus: "Explosive Power & Weak Points",
      calorieGoal: 2600,
      proteinGoal: 180,
      exercises: [
        {
          id: 1,
          name: "Box Jumps or Broad Jumps",
          sets: 5,
          reps: "3-5 reps",
          rest: "2 min",
          notes: "Power assessment - how explosive are you? Record jump height/distance.",
          howTo: "Box: Jump onto box, step down. Broad: Standing long jump, measure distance.",
          assessmentMetric: "lower_power",
          progressionTips: "Power development improves rate of force production for all lifts"
        },
        {
          id: 2,
          name: "Medicine Ball Slams or Chest Pass",
          sets: 4,
          reps: "8-10",
          rest: "90 sec",
          notes: "Upper body power test",
          howTo: "Slam: Overhead slam to ground. Pass: Explosive chest pass against wall.",
          assessmentMetric: "upper_power"
        },
        {
          id: 3,
          name: "Front Squat or Goblet Squat - Light",
          sets: 4,
          reps: "10 reps",
          rest: "2 min",
          notes: "Tests quad strength and core stability with front-loaded weight",
          howTo: "Bar in front rack OR heavy dumbbell at chest, squat deep",
          assessmentMetric: "front_squat_strength"
        },
        {
          id: 4,
          name: "Single Leg Romanian Deadlift",
          sets: 3,
          reps: "8 per leg",
          rest: "90 sec",
          notes: "Balance and unilateral strength - identify weak side",
          howTo: "One leg, hinge at hip, reach weight toward ground, feel hamstring",
          assessmentMetric: "unilateral_balance"
        },
        {
          id: 5,
          name: "Farmer's Walks",
          sets: 3,
          reps: "40-60 yards",
          rest: "2 min",
          notes: "Grip strength and loaded carry - fundamental for deadlift strength",
          howTo: "Heavy dumbbells or kettlebells, walk with upright posture",
          assessmentMetric: "grip_strength"
        },
        {
          id: 6,
          name: "Plank Variations (weighted if possible)",
          sets: 3,
          reps: "60 sec",
          rest: "90 sec",
          notes: "Core stability for bracing during max lifts",
          howTo: "Standard plank, add weight on back if too easy, or side planks"
        }
      ]
    },

    saturday: {
      name: "Active Recovery - Light Movement",
      time: "Flexible",
      duration: "30-40 min",
      location: "Home or Gym",
      focus: "Recovery & Mobility",
      calorieGoal: 2400,
      proteinGoal: 180,
      exercises: [
        {
          id: 1,
          name: "Light Cardio",
          sets: 1,
          reps: "20-30 min easy",
          rest: "N/A",
          notes: "Very light - walk, bike, row. Just movement for recovery.",
          howTo: "Conversational pace, promote blood flow"
        },
        {
          id: 2,
          name: "Mobility Work - Focus on Squat/Deadlift",
          sets: 1,
          reps: "15-20 min",
          rest: "N/A",
          notes: "Hip mobility, thoracic spine, ankle mobility. Better mobility = better lifts.",
          howTo: "Hip flexor stretches, deep squat holds, thoracic rotations, ankle mobilizations"
        }
      ]
    },

    sunday: {
      name: "Complete Rest",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Rest & Assessment Review",
      calorieGoal: 2400,
      proteinGoal: 180,
      exercises: [],
      recoveryNotes: "Review your lifts: Squat 3RM, Bench 3RM, Deadlift 3RM, OHP 5RM. These numbers will be used to calculate your personalized strength program percentages."
    }
  },

  // For users with primary goal: athletic performance / sports
  athletic: {
    programName: "Week 1: Athletic Performance Assessment",
    description: "This week tests your speed, power, agility, and sport-specific conditioning to build an athletic development program.",
    assessmentFocus: [
      "Speed and acceleration baseline",
      "Explosive power output",
      "Agility and change of direction",
      "Sport-specific conditioning and recovery"
    ],

    monday: {
      name: "Speed & Power Assessment",
      time: "Morning",
      duration: "50-60 min",
      location: "Track, Field, or Gym",
      focus: "Sprint Speed & Explosive Power",
      calorieGoal: 2500,
      proteinGoal: 170,
      nutritionNotes: "Training day - carbs for explosive performance",
      exercises: [
        {
          id: 1,
          name: "40-Yard Dash (or 40-meter)",
          sets: 3,
          reps: "1 sprint",
          rest: "3-5 min full recovery",
          notes: "Time your 40-yard sprint. This is a key athletic marker. Full recovery between attempts.",
          howTo: "Sprint start, maximum effort for 40 yards. Time it (phone app or partner). Take best of 3.",
          assessmentMetric: "sprint_speed",
          progressionTips: "Record time. Athletic programs will focus on improving this via power and technique."
        },
        {
          id: 2,
          name: "10-Yard Acceleration Test",
          sets: 3,
          reps: "1 sprint",
          rest: "2-3 min",
          notes: "First 10 yards of sprint - tests acceleration and starting power",
          howTo: "Sprint start, max effort for 10 yards, time it",
          assessmentMetric: "acceleration"
        },
        {
          id: 3,
          name: "Vertical Jump Test",
          sets: 3,
          reps: "1 max jump",
          rest: "2 min",
          notes: "Mark wall at standing reach, jump and touch highest point. Measure difference.",
          howTo: "Stand beside wall, chalk on fingers, jump as high as possible and touch wall. Best of 3.",
          assessmentMetric: "vertical_jump",
          progressionTips: "VJ correlates with lower body power - key for most sports"
        },
        {
          id: 4,
          name: "Broad Jump (Standing Long Jump)",
          sets: 3,
          reps: "1 max jump",
          rest: "2 min",
          notes: "Horizontal power test - measure distance from start to landing",
          howTo: "Feet together, swing arms, jump forward as far as possible. Measure from start line to heel.",
          assessmentMetric: "broad_jump"
        },
        {
          id: 5,
          name: "Box Jumps - Max Height",
          sets: 5,
          reps: "1 jump",
          rest: "2 min",
          notes: "Find your max box jump height. Build up height each set.",
          howTo: "Start low (20\"), work up to max height you can safely land on. Record highest box.",
          assessmentMetric: "reactive_power"
        },
        {
          id: 6,
          name: "Trap Bar Deadlift or Power Clean - Explosive",
          sets: 5,
          reps: "3 reps (moderate weight, fast)",
          rest: "2-3 min",
          notes: "If available, test explosive pulling strength. Focus on speed, not max weight.",
          howTo: "Trap bar: Explode up fast. Power clean: Pull and catch at shoulders. Moderate weight, maximum speed.",
          assessmentMetric: "explosive_strength"
        }
      ]
    },

    tuesday: {
      name: "Agility & Change of Direction",
      time: "Morning",
      duration: "45-55 min",
      location: "Field, Court, or Gym",
      focus: "Agility, COD, Reactive Ability",
      calorieGoal: 2500,
      proteinGoal: 170,
      exercises: [
        {
          id: 1,
          name: "Pro Agility (5-10-5 Shuttle)",
          sets: 3,
          reps: "1 run",
          rest: "3 min",
          notes: "Start at middle cone, run 5 yards right and touch line, run 10 yards left and touch, run 5 yards back to start. Time it.",
          howTo: "Set up 3 cones 5 yards apart. Touch lines with hand, not just foot. Best of 3 times.",
          assessmentMetric: "agility",
          progressionTips: "Key COD (change of direction) test for all field/court sports"
        },
        {
          id: 2,
          name: "L-Drill (3-Cone Drill)",
          sets: 3,
          reps: "1 run",
          rest: "3 min",
          notes: "Set up L-shape with cones. Sprint, change direction, sprint back. Time it.",
          howTo: "Cones in L-shape 5 yards apart. Sprint to first, around second, back to first, around all cones. Time it.",
          assessmentMetric: "directional_agility"
        },
        {
          id: 3,
          name: "Lateral Shuffle - 20 yards",
          sets: 4,
          reps: "2 lengths (there and back)",
          rest: "90 sec",
          notes: "Lateral movement speed - critical for defense in sports",
          howTo: "Athletic stance, shuffle 20 yards without crossing feet, shuffle back. Time if possible.",
          assessmentMetric: "lateral_speed"
        },
        {
          id: 4,
          name: "Reactive Agility Drill",
          sets: 5,
          reps: "30 seconds",
          rest: "90 sec",
          notes: "Set up 4-5 cones randomly. Sprint to random cone, decelerate, change direction to next cone. Max effort.",
          howTo: "Quick bursts, practice deceleration and re-acceleration, explosive COD",
          assessmentMetric: "reactive_ability"
        },
        {
          id: 5,
          name: "Ladder Drills (if available) or Quick Feet",
          sets: 4,
          reps: "2 lengths",
          rest: "60 sec",
          notes: "Foot speed and coordination test",
          howTo: "Agility ladder: Various patterns (in-out, icky shuffle, lateral, etc) OR quick feet drill",
          assessmentMetric: "foot_speed"
        },
        {
          id: 6,
          name: "Deceleration Drill - Sprint to Stop",
          sets: 4,
          reps: "20 yard sprint",
          rest: "90 sec",
          notes: "Sprint 20 yards, decelerate and stop as quickly as possible in 3-5 yards. Tests eccentric strength.",
          howTo: "Max sprint, plant and stop within marked zone. Develop braking ability.",
          assessmentMetric: "deceleration_ability"
        }
      ]
    },

    wednesday: {
      name: "Strength Foundation Assessment",
      time: "Morning",
      duration: "55-65 min",
      location: "Gym",
      focus: "Athletic Strength Baseline",
      calorieGoal: 2500,
      proteinGoal: 170,
      exercises: [
        {
          id: 1,
          name: "Back Squat or Front Squat - 5RM",
          sets: 4,
          reps: "Build to heavy 5 reps",
          rest: "3 min",
          notes: "Lower body strength baseline - find your 5-rep max",
          howTo: "Build up weight, find heaviest 5 reps with good form",
          assessmentMetric: "squat_strength"
        },
        {
          id: 2,
          name: "Bench Press - 5RM",
          sets: 4,
          reps: "Build to heavy 5 reps",
          rest: "3 min",
          notes: "Upper body pressing baseline",
          howTo: "Build to heavy 5 reps, touch chest, full lockout",
          assessmentMetric: "press_strength"
        },
        {
          id: 3,
          name: "Pull-ups or Rows - Max Effort",
          sets: 3,
          reps: "Max pull-ups OR heavy rows x8",
          rest: "2 min",
          notes: "Pulling strength baseline",
          howTo: "Dead hang pull-ups to max OR heavy barbell rows",
          assessmentMetric: "pull_strength"
        },
        {
          id: 4,
          name: "Single Leg Squat Assessment",
          sets: 3,
          reps: "8-10 per leg",
          rest: "90 sec",
          notes: "Unilateral strength and balance - key for athletics",
          howTo: "Bulgarian split squats or pistol squat progressions, note imbalances",
          assessmentMetric: "unilateral_strength"
        },
        {
          id: 5,
          name: "Rotational Med Ball Throw",
          sets: 3,
          reps: "5 per side",
          rest: "90 sec",
          notes: "Rotational power - critical for throwing/hitting sports",
          howTo: "Stand sideways to wall, rotate and throw med ball explosively against wall",
          assessmentMetric: "rotational_power"
        },
        {
          id: 6,
          name: "Core - Anti-Rotation Press",
          sets: 3,
          reps: "10 per side",
          rest: "60 sec",
          notes: "Core stability for athletic movements",
          howTo: "Cable or band at chest, press out and resist rotation, pull back"
        }
      ]
    },

    thursday: {
      name: "Rest Day",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Recovery from Testing",
      calorieGoal: 2300,
      proteinGoal: 170,
      nutritionNotes: "Rest day - quality sleep is crucial for athletic performance",
      exercises: [],
      recoveryNotes: "How's your recovery from speed/agility work? Note muscle soreness, especially quads/hamstrings/calves."
    },

    friday: {
      name: "Conditioning & Work Capacity",
      time: "Morning",
      duration: "45-55 min",
      location: "Track or Gym",
      focus: "Sport-Specific Conditioning Baseline",
      calorieGoal: 2500,
      proteinGoal: 170,
      exercises: [
        {
          id: 1,
          name: "300-Yard Shuttle Run",
          sets: 1,
          reps: "1 max effort",
          rest: "5+ min after",
          notes: "Classic conditioning test. 25 yards down and back = 50 yards. Repeat 6 times = 300 yards. Time it.",
          howTo: "Sprint 25 yards, touch line, sprint back. Repeat 6 times total. Record time.",
          assessmentMetric: "anaerobic_capacity",
          progressionTips: "Brutal but effective test. <60 sec = elite, 60-70 = good, 70+ = needs work"
        },
        {
          id: 2,
          name: "Repeated Sprint Ability Test",
          sets: 6,
          reps: "40 yard sprint",
          rest: "30 sec between sprints",
          notes: "Tests ability to repeat high-intensity efforts with short rest - mimics game situations",
          howTo: "Sprint 40 yards max effort, walk back (30 sec rest), repeat. Track time drop-off from first to last.",
          assessmentMetric: "repeat_sprint_ability",
          progressionTips: "Can you maintain speed? Sprint 6 should be <10% slower than sprint 1"
        },
        {
          id: 3,
          name: "Beep Test (Yo-Yo Test) - Option if available",
          sets: 1,
          reps: "To exhaustion",
          rest: "Complete",
          notes: "Progressive shuttle run test - run between lines matching beeps that get progressively faster",
          howTo: "Download beep test app, run 20m shuttles matching beeps. Record level/stage reached.",
          assessmentMetric: "aerobic_power"
        },
        {
          id: 4,
          name: "Assault Bike or Rower - 500m Time Trial",
          sets: 1,
          reps: "500 meters max effort",
          rest: "5 min",
          notes: "Alternative conditioning test if no track available",
          howTo: "Max effort 500m, record time. Assault bike: aim <90 sec, Rower: aim <1:45",
          assessmentMetric: "conditioning_baseline"
        },
        {
          id: 5,
          name: "Burpee Broad Jumps",
          sets: 3,
          reps: "10 reps",
          rest: "2 min",
          notes: "Power endurance - burpee into broad jump",
          howTo: "Burpee, then broad jump forward, repeat continuously",
          assessmentMetric: "power_endurance"
        },
        {
          id: 6,
          name: "Core Circuit - Timed",
          sets: 3,
          reps: "45 sec each exercise",
          rest: "15 sec transition",
          notes: "Plank, side plank R, side plank L, bicycle crunches. No rest between.",
          howTo: "Circuit through all 4 core exercises, minimal rest, repeat 3 rounds"
        }
      ]
    },

    saturday: {
      name: "Sport-Specific Skills or Active Recovery",
      time: "Flexible",
      duration: "45-60 min",
      location: "Field, Court, or Sport Facility",
      focus: "Sport Practice & Recovery",
      calorieGoal: 2500,
      proteinGoal: 170,
      exercises: [
        {
          id: 1,
          name: "Sport-Specific Skills Practice",
          sets: 1,
          reps: "45-60 min",
          rest: "N/A",
          notes: "Basketball shooting, soccer dribbling, baseball batting practice, etc. Note skills that need work.",
          howTo: "Practice your sport at moderate intensity, focus on skills and technique",
          assessmentMetric: "sport_specific"
        },
        {
          id: 2,
          name: "Mobility & Flexibility Work",
          sets: 1,
          reps: "15-20 min",
          rest: "N/A",
          notes: "Focus on sport-specific mobility - hip mobility for field sports, shoulder for throwing, etc.",
          howTo: "Dynamic stretching, foam rolling, yoga flows"
        }
      ]
    },

    sunday: {
      name: "Complete Rest",
      time: "All Day",
      duration: "N/A",
      location: "Recovery",
      focus: "Rest & Assessment Review",
      calorieGoal: 2300,
      proteinGoal: 170,
      exercises: [],
      recoveryNotes: "Review your athletic testing: Sprint times, vertical jump, agility times, conditioning performance. This data creates your sport-specific training program."
    }
  }
};

// Helper function to check if exercise requires gym equipment
function requiresGymEquipment(exerciseName) {
  const gymKeywords = [
    'barbell', 'machine', 'cable', 'leg press', 'smith machine',
    'trap bar', 'rack', 'bench press', 'power clean', 'box jump'
  ];
  const lowerName = exerciseName.toLowerCase();
  return gymKeywords.some(keyword => lowerName.includes(keyword));
}

// Helper function to adapt a full exercise object for location and equipment
function adaptExerciseForLocation(exercise, availableEquipment, isGymDay) {
  const exerciseName = exercise.name;
  const hasDumbbells = availableEquipment.includes('dumbbells');
  const hasKettlebells = availableEquipment.includes('kettlebells');
  const hasBands = availableEquipment.includes('resistance_bands');
  const hasBench = availableEquipment.includes('bench') || availableEquipment.includes('weight_bench');
  const hasPullUpBar = availableEquipment.includes('pull_up_bar');
  const hasWeights = hasDumbbells || hasKettlebells;

  // If it's a gym day, return exercise as-is (gym has all equipment)
  if (isGymDay) {
    return exercise;
  }

  // Define comprehensive exercise substitutions for home workouts
  const exerciseSubstitutions = {
    // CHEST EXERCISES
    'Barbell Bench Press - 1RM Estimate': hasWeights && hasBench
      ? { name: 'Dumbbell Bench Press - Heavy 3-5 Reps', notes: 'Home adaptation: Using dumbbells instead of barbell. ' + (exercise.notes || '') }
      : { name: 'Decline Push-ups - Max Effort', notes: 'Home adaptation: Bodyweight substitute for bench press. Feet elevated for intensity. ' + (exercise.notes || ''), reps: 'MAX (aim for 8-12)' },

    'Bench Press - Volume Drop Set': hasWeights && hasBench
      ? { name: 'Dumbbell Bench Press', notes: exercise.notes }
      : { name: 'Standard Push-ups', notes: 'Home adaptation: Bodyweight pushing. Add tempo (3 sec down, 1 sec up) for difficulty. ' + (exercise.notes || '') },

    'Dumbbell Bench Press or Floor Press (Home)': hasWeights
      ? exercise
      : { name: 'Decline Push-ups (feet elevated)', notes: 'Home adaptation: Bodyweight chest exercise. ' + (exercise.notes || '') },

    'Dumbbell or Machine Chest Press': hasWeights
      ? { name: 'Dumbbell Floor Press', notes: 'Using floor instead of bench. ' + (exercise.notes || '') }
      : { name: 'Push-ups (various tempos)', notes: 'Home adaptation: Vary tempo for intensity. ' + (exercise.notes || '') },

    'Incline Dumbbell Press': hasWeights
      ? { name: 'Floor Press or Decline Push-ups', notes: 'Adapted for home. ' + (exercise.notes || '') }
      : { name: 'Pike Push-ups', notes: 'Upper chest emphasis. ' + (exercise.notes || '') },

    // SHOULDER EXERCISES
    'Overhead Press (Dumbbells or Machine)': hasWeights
      ? { name: 'Dumbbell Overhead Press', notes: exercise.notes }
      : { name: 'Pike Push-ups', notes: 'Home adaptation: Bodyweight shoulder press. ' + (exercise.notes || '') },

    'Barbell Overhead Press - 5RM Test': hasWeights
      ? { name: 'Dumbbell Overhead Press - Heavy', notes: 'Home adaptation using dumbbells. ' + (exercise.notes || '') }
      : { name: 'Handstand Hold or Pike Push-ups', notes: 'Advanced bodyweight shoulder work. ' + (exercise.notes || '') },

    'Lateral Raises': hasWeights || hasBands
      ? exercise
      : { name: 'Plank to Down Dog', notes: 'Home adaptation: Shoulder activation without weights. ' + (exercise.notes || '') },

    'Face Pulls': hasBands
      ? exercise
      : { name: 'Reverse Snow Angels (lying face down)', notes: 'Home adaptation: Rear delt activation. ' + (exercise.notes || '') },

    // LEG EXERCISES
    'Barbell Back Squat - 1RM Estimate': hasWeights
      ? { name: 'Goblet Squat - Heavy', notes: 'Home adaptation with dumbbells. ' + (exercise.notes || '') }
      : { name: 'Bulgarian Split Squats (bodyweight)', notes: 'Home adaptation: Single leg for intensity. ' + (exercise.notes || ''), reps: '12-15 per leg' },

    'Barbell Back Squat - 5RM': hasWeights
      ? { name: 'Goblet Squat - Heavy', notes: 'Home adaptation. ' + (exercise.notes || '') }
      : { name: 'Jump Squats', notes: 'Explosive bodyweight variation. ' + (exercise.notes || '') },

    'Goblet Squat or Leg Press': hasWeights
      ? { name: 'Goblet Squat', notes: exercise.notes }
      : { name: 'Bodyweight Squats - High Volume', notes: 'Home adaptation: Use tempo or single leg variations. ' + (exercise.notes || ''), reps: '20-30' },

    'Leg Press - High Rep Test': hasWeights
      ? { name: 'Goblet Squat - High Reps', notes: 'Home adaptation. ' + (exercise.notes || '') }
      : { name: 'Wall Sit (timed)', notes: 'Isometric leg work. Aim for 60+ seconds. ' + (exercise.notes || ''), reps: '60-90 sec' },

    'Romanian Deadlift (Dumbbells or Barbell)': hasWeights
      ? { name: 'Dumbbell Romanian Deadlift', notes: exercise.notes }
      : { name: 'Single-leg RDL (bodyweight)', notes: 'Home adaptation: Balance challenge increases difficulty. ' + (exercise.notes || '') },

    'Conventional Deadlift - 1RM Estimate': hasWeights
      ? { name: 'Dumbbell Deadlift - Heavy', notes: 'Home adaptation with dumbbells. ' + (exercise.notes || '') }
      : { name: 'Nordic Hamstring Curls or Single-leg RDL', notes: 'Advanced hamstring work. ' + (exercise.notes || ''), reps: '5-8' },

    'Deficit Deadlifts or Romanian Deadlifts': hasWeights
      ? { name: 'Single-leg Dumbbell RDL', notes: 'Home adaptation. ' + (exercise.notes || '') }
      : { name: 'Single-leg RDL (bodyweight) - Slow Tempo', notes: 'Emphasize hamstring stretch. ' + (exercise.notes || '') },

    'Leg Curl (Machine or Nordic if advanced)': null // Will be handled by machine check
      ? { name: 'Lying Leg Curls (if available)', notes: exercise.notes }
      : { name: 'Glute Ham Raises or Nordic Curls (partner assisted)', notes: 'Home adaptation: Very challenging hamstring exercise. ' + (exercise.notes || '') },

    // BACK EXERCISES
    'Barbell Row - Working Weight Test': hasWeights
      ? { name: 'Dumbbell Bent-Over Rows', notes: 'Home adaptation. ' + (exercise.notes || '') }
      : { name: 'Inverted Rows (under table)', notes: 'Home adaptation using sturdy table or chairs. ' + (exercise.notes || '') },

    'Barbell or Dumbbell Row': hasWeights
      ? { name: 'Dumbbell Rows', notes: exercise.notes }
      : { name: 'Inverted Rows', notes: 'Home adaptation. ' + (exercise.notes || '') },

    'Seated Cable Row or Inverted Rows': hasBands
      ? { name: 'Band Rows', notes: 'Using resistance bands. ' + (exercise.notes || '') }
      : { name: 'Inverted Rows (under table)', notes: 'Home adaptation. ' + (exercise.notes || '') },

    'Pull-ups or Chin-ups - Max Rep Test': hasPullUpBar
      ? exercise
      : { name: 'Inverted Rows - Max Reps', notes: 'Home adaptation: Horizontal pulling. Set up under sturdy table. ' + (exercise.notes || '') },

    'Pull-ups or Weighted Pull-ups': hasPullUpBar
      ? exercise
      : { name: 'Inverted Rows - Elevated Feet', notes: 'Home adaptation: Feet elevated increases difficulty. ' + (exercise.notes || '') },

    'Pull-ups - Volume Test (or Negative Pull-ups)': hasPullUpBar
      ? exercise
      : { name: 'Inverted Rows - Volume', notes: 'Home adaptation. ' + (exercise.notes || '') },

    'Dumbbell Rows': hasWeights
      ? exercise
      : { name: 'Reverse Snow Angels (lying)', notes: 'Back activation without weights. ' + (exercise.notes || '') },

    // TRICEPS
    'Dips (weighted if possible)': null // Check for dip station
      ? { name: 'Dips', notes: exercise.notes }
      : { name: 'Chair/Bench Dips or Diamond Push-ups', notes: 'Home adaptation: Use sturdy chair or diamond push-ups. ' + (exercise.notes || '') },

    'Tricep Dips or Pushdowns': null
      ? exercise
      : { name: 'Chair Dips or Diamond Push-ups', notes: 'Home adaptation. ' + (exercise.notes || '') },

    'Tricep Work (Close Grip Bench or Pushdowns)': hasWeights
      ? { name: 'Close Grip Floor Press', notes: 'Home adaptation. ' + (exercise.notes || '') }
      : { name: 'Diamond Push-ups', notes: 'Tricep-focused push-up variation. ' + (exercise.notes || '') },

    // BICEPS
    'Barbell or Dumbbell Curls': hasWeights
      ? { name: 'Dumbbell Curls', notes: exercise.notes }
      : { name: 'Chin-up Hold or Resistance Band Curls', notes: 'Home adaptation. ' + (exercise.notes || '') },

    'Bicep Curls (Barbell or Dumbbell)': hasWeights
      ? { name: 'Dumbbell Curls', notes: exercise.notes }
      : hasBands
      ? { name: 'Resistance Band Curls', notes: 'Using bands. ' + (exercise.notes || '') }
      : { name: 'Chin-up Negatives', notes: 'Eccentric bicep work. ' + (exercise.notes || '') },

    // OLYMPIC LIFTS / POWER
    'Trap Bar Deadlift or Power Clean - Explosive': hasWeights
      ? { name: 'Dumbbell Hang Clean', notes: 'Home adaptation for power. ' + (exercise.notes || '') }
      : { name: 'Broad Jumps or Jump Squats', notes: 'Explosive power training. ' + (exercise.notes || '') },

    'Medicine Ball Slams or Chest Pass': hasWeights || hasBands
      ? { name: 'Explosive Push-ups or Slam Alternatives', notes: 'Home adaptation. ' + (exercise.notes || '') }
      : { name: 'Clapping Push-ups or Explosive Squats', notes: 'Plyometric alternatives. ' + (exercise.notes || '') },

    // ACCESSORY
    'Bulgarian Split Squats': hasWeights
      ? exercise
      : { ...exercise, notes: 'Bodyweight version - add tempo for difficulty. ' + (exercise.notes || '') },

    'Walking Lunges': hasWeights
      ? exercise
      : { ...exercise, notes: 'Bodyweight - add jump lunges for advanced. ' + (exercise.notes || '') },

    'Farmer\'s Walks': hasWeights
      ? exercise
      : { name: 'Plank Shoulder Taps', notes: 'Core and stability work. ' + (exercise.notes || ''), reps: '30-40 taps' }
  };

  // Check if we have a direct substitution
  if (exerciseSubstitutions[exerciseName]) {
    const substitution = exerciseSubstitutions[exerciseName];
    if (substitution === null) {
      // Handle special cases that need equipment checks
      if (exerciseName.includes('Leg Curl')) {
        return {
          ...exercise,
          name: 'Nordic Curls (partner assisted) or Glute Ham Raises',
          notes: 'Home adaptation: Very challenging hamstring exercise. Use partner or anchor feet. ' + (exercise.notes || '')
        };
      }
      if (exerciseName.includes('Dip')) {
        return {
          ...exercise,
          name: availableEquipment.includes('dip_station') ? exerciseName : 'Chair Dips or Diamond Push-ups',
          notes: availableEquipment.includes('dip_station') ? exercise.notes : 'Home adaptation using chair. ' + (exercise.notes || '')
        };
      }
    }
    return { ...exercise, ...substitution };
  }

  // If no substitution found but exercise requires gym, provide a generic adaptation note
  if (requiresGymEquipment(exerciseName)) {
    return {
      ...exercise,
      notes: '⚠️ This exercise typically requires gym equipment. Adapt as needed with available equipment. ' + (exercise.notes || '')
    };
  }

  // Return original exercise if no adaptation needed
  return exercise;
}

// Helper function to get assessment week based on user goal, equipment, and schedule
export function getAssessmentWeek(primaryGoal, userEquipment = [], gymDays = [], gymAccess = []) {
  const goalMapping = {
    'muscle': 'muscle',
    'strength': 'strength',
    'weight_loss': 'fatLoss',
    'toned': 'fatLoss',
    'athletic': 'athletic',
    'general_fitness': 'fatLoss'
  };

  const assessmentType = goalMapping[primaryGoal] || 'muscle';
  const baseAssessment = assessmentWeeks[assessmentType];

  // Deep clone for modifications
  const adaptedAssessment = JSON.parse(JSON.stringify(baseAssessment));

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Check if user has any gym access (not just "no_gym")
  const hasGymAccess = gymAccess.length > 0 && !gymAccess.includes('no_gym');

  // Check if user has home weights
  const hasHomeWeights = userEquipment.includes('dumbbells') || userEquipment.includes('kettlebells');

  days.forEach(day => {
    if (adaptedAssessment[day] && adaptedAssessment[day].exercises) {
      const isGymDay = gymDays.includes(day);

      // Determine what equipment is available this day
      let availableEquipment;
      let locationLabel;

      if (isGymDay && hasGymAccess) {
        // On gym days, assume full gym equipment available
        availableEquipment = ['dumbbells', 'kettlebells', 'resistance_bands', 'machines', 'barbells', 'cable_machine', 'leg_press', 'bench'];
        locationLabel = 'Gym';
      } else if (hasHomeWeights) {
        // Home day with weights available
        availableEquipment = userEquipment;
        locationLabel = 'Home (with weights)';
      } else {
        // Home day, bodyweight only
        availableEquipment = userEquipment.filter(e => !['dumbbells', 'kettlebells'].includes(e));
        locationLabel = 'Home (bodyweight)';
      }

      // Adapt exercises based on location and available equipment
      adaptedAssessment[day].exercises = adaptedAssessment[day].exercises.map(exercise => {
        const adaptedExercise = adaptExerciseForLocation(exercise, availableEquipment, isGymDay && hasGymAccess);
        return adaptedExercise;
      });

      // Update workout location with specific label
      if (adaptedAssessment[day].location) {
        adaptedAssessment[day].location = locationLabel;
        // Add a note about the location to the workout
        if (!isGymDay && hasGymAccess) {
          adaptedAssessment[day].locationNote = 'This is a home workout day. Exercises have been adapted for home training.';
        } else if (isGymDay && hasGymAccess) {
          adaptedAssessment[day].locationNote = 'This is a gym workout day. Full gym equipment is available.';
        }
      }
    }
  });

  return adaptedAssessment;
}
